import apiService from './api';

export interface LoginData {
  email: string;
  password: string;
}

export interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  phoneNumber?: string;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  token?: string;
  user?: {
    id: number;
    name: string;
    email: string;
    phoneNumber?: string;
  };
}

export interface UserProfile {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  address?: string;
}

class AuthService {
  async login(credentials: LoginData): Promise<AuthResponse> {
    try {
      const response = await apiService.post<AuthResponse>('/auth/login', credentials);
      if (response.token) {
        localStorage.setItem('authToken', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
      }
      return response;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Login failed');
    }
  }

  async register(userData: RegisterData): Promise<AuthResponse> {
    try {
      const response = await apiService.post<AuthResponse>('/auth/register', userData);
      if (response.token) {
        localStorage.setItem('authToken', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
      }
      return response;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Registration failed');
    }
  }

  async logout(): Promise<void> {
    try {
      await apiService.post('/auth/logout');
    } catch (error) {
      // Error handled silently
    } finally {
      // Clear all authentication and user data
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      
      // Clear any cart and wishlist related data that might persist
      localStorage.removeItem('cartItems');
      localStorage.removeItem('wishlistItems');
      localStorage.removeItem('cartCount');
      localStorage.removeItem('wishlistCount');
      
      // Clear any other user-specific data
      localStorage.removeItem('userPreferences');
      localStorage.removeItem('recentSearches');
    }
  }

  async getProfile(): Promise<UserProfile> {
    try {
      const response = await apiService.get<{ success: boolean; user: UserProfile }>('/auth/profile');
      return response.user;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch profile');
    }
  }

  async refreshToken(): Promise<{ token: string }> {
    try {
      const response = await apiService.post<{ success: boolean; token: string }>('/auth/refresh');
      if (response.token) {
        localStorage.setItem('authToken', response.token);
      }
      return { token: response.token };
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Token refresh failed');
    }
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    try {
      return await apiService.post<{ success: boolean; message: string }>('/auth/forgot-password', { email });
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to send reset link');
    }
  }

  async resetPassword(token: string, newPassword: string, confirmPassword: string): Promise<{ message: string }> {
    try {
      return await apiService.post<{ success: boolean; message: string }>('/auth/reset-password', { token, newPassword, confirmPassword });
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to reset password');
    }
  }

  isAuthenticated(): boolean {
    const token = localStorage.getItem('authToken');
    return !!token;
  }

  getCurrentUser(): UserProfile | null {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  getToken(): string | null {
    return localStorage.getItem('authToken');
  }
}

export const authService = new AuthService();
export default authService;
